import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

/**
 * 角丸も影も使わない。面の区切りは 1px のボーダーだけ。
 * (トークン側で --radius-* / --shadow-* を潰してあるので rounded-* を書いても平らになるが、
 *  意図を明示するためここでも付けない)
 */
const buttonVariants = cva(
  // 透明のボーダーは飾りではなく高さ合わせ。これが無いと outline だけ 2px 高くなり、
  // 横並びの行で入力欄と上端が揃わない (実際に mb-0.5 のような手当てが生えていた)。
  'inline-flex items-center justify-center gap-2 border border-transparent ' +
    'whitespace-nowrap text-sm font-medium transition-colors ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700 ' +
    'disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        /** 主要アクション */
        primary: 'bg-brand-700 text-surface-raised hover:bg-brand-800',
        /** 中立的な操作 */
        outline: 'border border-line-bold text-ink-secondary hover:bg-surface',
        ghost: 'text-ink-muted hover:text-ink hover:bg-surface',
        /** 破壊的・警告 */
        alert: 'bg-alert text-surface-raised hover:opacity-90',
      },
      size: {
        sm: 'px-3 py-2',
        md: 'px-4 py-2.5',
      },
    },
    defaultVariants: { variant: 'outline', size: 'sm' },
  },
);

export type ButtonProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants>;

export function Button({ className, variant, size, ...props }: ButtonProps) {
  return <button className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

export { buttonVariants };
